import { Check, Film } from 'lucide-react'

import type { ShopMedia } from '@/lib/api/shop-media-api'
import { formatBytes } from '@/components/media/media-detail-sheet'
import { LoadMoreSentinel } from '@/components/media/load-more-sentinel'
import { isVideo } from '@/lib/api/shop-media-api'
import { thumbOf } from '@/lib/api/shop-catalog-api'
import { m } from '@/lib/paraglide/messages'
import { cn, twJoin } from '@/lib/utils'

interface Props {
    items: ShopMedia[]
    /** id đang chọn (chế độ chọn) hoặc đang mở chi tiết. */
    selected?: ReadonlySet<string>
    onPick: (item: ShopMedia) => void
    hasMore: boolean
    loadingMore: boolean
    onLoadMore: () => void
    className?: string
}

/** Lưới ảnh / video của thư viện; cuộn tới đáy tự tải trang sau. */
export function MediaGrid({
    items,
    selected,
    onPick,
    hasMore,
    loadingMore,
    onLoadMore,
    className,
}: Props) {
    return (
        <div className={cn('space-y-3', className)}>
            <ul
                className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                aria-label={m.media_grid_label()}
            >
                {items.map((item) => (
                    <li key={item.id}>
                        <MediaTile
                            item={item}
                            active={selected?.has(item.id) ?? false}
                            onPick={onPick}
                        />
                    </li>
                ))}
            </ul>
            <LoadMoreSentinel
                hasMore={hasMore}
                loadingMore={loadingMore}
                onLoadMore={onLoadMore}
            />
        </div>
    )
}

function MediaTile({
    item,
    active,
    onPick,
}: {
    item: ShopMedia
    active: boolean
    onPick: (item: ShopMedia) => void
}) {
    const video = isVideo(item)
    const name = item.alt || item.filename
    return (
        <button
            type="button"
            aria-pressed={active}
            aria-label={name}
            title={item.filename}
            onClick={() => onPick(item)}
            className={twJoin(
                'group relative block w-full cursor-pointer overflow-hidden rounded-md border bg-muted text-left',
                'transition-colors duration-150 hover:border-primary',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                active && 'border-primary ring-2 ring-primary',
            )}
        >
            <span className="flex aspect-square items-center justify-center">
                {video ? (
                    <Film
                        className="size-8 text-muted-foreground"
                        aria-hidden
                    />
                ) : (
                    <img
                        src={thumbOf(item.url)}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                    />
                )}
            </span>
            <span className="block truncate border-t bg-background px-2 py-1 text-xs">
                {item.filename}
            </span>
            {video && (
                // Dung lượng giúp phân biệt nhiều file mp4 mà không tải byte
                // video nào (không dựng thẻ video cho cả lưới).
                <span className="absolute bottom-8 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
                    {formatBytes(item.size)}
                </span>
            )}
            {!item.alt && !video && (
                <span className="absolute left-1 top-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900">
                    {m.media_missing_alt()}
                </span>
            )}
            {active && (
                <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                    <Check className="size-3.5" aria-hidden />
                </span>
            )}
        </button>
    )
}
